import { createContext, useContext, useMemo } from "react";
import { makeEmojiCache } from "../../../../modules/emoji/emoji-cache";
import { propertyNotEmpty } from "../../../../utils/not-empty";

import type { EmojiCache } from "../../../../modules/emoji/emoji-cache";
import type { MomentAnalyticsTotals } from "../../../../modules/stats/server/analytics-service";
import type { UseSupabaseLoaderState } from "../../../../server/supabase/hooks/types";
import type { FC, ReactNode } from "react";

interface MomentStatsTotalsContextType {
  emojiCache: EmojiCache;

  totals?: UseSupabaseLoaderState<MomentAnalyticsTotals>;
}

const MomentAnalyticsContext = createContext<MomentStatsTotalsContextType | null>(null);

type MomentAnalyticsProviderProps = Omit<MomentStatsTotalsContextType, "emojiCache"> & {
  children: ReactNode;
};

export const MomentAnalyticsProvider: FC<MomentAnalyticsProviderProps> = ({ totals, children }) => {
  const emojiCache = useMemo(() => {
    if (!totals || totals.loading || totals.error) return {};

    const emojis = Object.entries(totals.data.reaction_summary).map(([emoji, { custom_url }]) => ({
      emoji,
      custom_url,
    }));

    return makeEmojiCache(
      emojis.map(({ emoji }) => emoji),
      Object.fromEntries(
        emojis
          .filter(propertyNotEmpty("custom_url"))
          .map(({ emoji, custom_url }) => [emoji, custom_url]),
      ),
    );
  }, [totals]);

  return (
    // oxlint-disable-next-line react/jsx-no-constructed-context-values -- Context value is rebuilt each render; memoising it is a separate change
    <MomentAnalyticsContext.Provider value={{ totals, emojiCache }}>
      {children}
    </MomentAnalyticsContext.Provider>
  );
};

export const useMomentAnalyticsContext = (): MomentStatsTotalsContextType => {
  const context = useContext(MomentAnalyticsContext);
  if (!context) {
    throw new Error("useMomentAnalyticsContext be used within a MomentAnalyticsProvider");
  }
  return context;
};
